const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const REFERENCE = new RegExp(`^storage://(checklist-partner-media)/(${UUID})/(${UUID})/(${UUID}\\.(?:jpg|png|webp))$`, 'i');

export const isStorageMediaReference = (value?: string | null): boolean => /^storage:/i.test(value ?? '');

/** Aceita apenas o contrato canônico; não decodifica caminhos ou segmentos extras. */
export const parsePartnerStorageReference = (value: string) => {
  const match = REFERENCE.exec(value);
  if (!match || match[1] !== 'checklist-partner-media') return null;
  return { bucket: match[1], companyId: match[2], checklistId: match[3], path: `${match[2]}/${match[3]}/${match[4]}` };
};

export interface PartnerMediaIdentity { userId: string; companyId: string; sessionKey: string }
export interface PartnerMediaScope { companyId: string; checklistId: string }
export const PARTNER_MEDIA_URL_LIFETIME_SECONDS = 300;
const MAX_CACHE_ENTRIES = 100;

/** URLs assinadas vivem somente neste cache, isoladas por sessão e com margem de expiração. */
export const createPartnerMediaResolver = (dependencies: {
  identity(): PartnerMediaIdentity | null;
  sign(bucket: string, path: string, identity: PartnerMediaIdentity): Promise<string>;
  now?(): number;
}) => {
  const cache = new Map<string, { url: string; expiresAt: number }>();
  const now = dependencies.now ?? Date.now;
  let owner = '';
  const identityKey = (identity: PartnerMediaIdentity | null) => identity
    ? JSON.stringify([identity.userId, identity.companyId, identity.sessionKey]) : '';
  return async (value: string, scope?: PartnerMediaScope): Promise<string> => {
    if (!isStorageMediaReference(value)) return value;
    const identity = dependencies.identity();
    const key = identityKey(identity);
    if (owner !== key) { cache.clear(); owner = key; }
    const ref = parsePartnerStorageReference(value);
    if (!ref || !identity || !identity.userId || !identity.sessionKey || ref.companyId !== identity.companyId ||
        (scope && (scope.companyId !== ref.companyId || scope.checklistId !== ref.checklistId))) {
      throw new Error('Mídia privada inválida ou fora da identidade atual.');
    }
    const cached = cache.get(value);
    if (cached && cached.expiresAt > now()) return cached.url;
    cache.delete(value);
    const startedAt = now();
    const url = await dependencies.sign(ref.bucket, ref.path, identity);
    if (identityKey(dependencies.identity()) !== key) throw new Error('A sessão mudou ao carregar a mídia privada.');
    if (!/^https?:\/\//i.test(url)) throw new Error('URL de mídia privada inválida.');
    const expiresAt = startedAt + (PARTNER_MEDIA_URL_LIFETIME_SECONDS - 30) * 1000;
    if (expiresAt <= now()) throw new Error('A URL da mídia privada expirou.');
    while (cache.size >= MAX_CACHE_ENTRIES) cache.delete(cache.keys().next().value!);
    cache.set(value, { url, expiresAt });
    return url;
  };
};
